import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Cookies from 'js-cookie';

import { useLocation, useNavigate } from 'react-router-dom';

import {
  Button,
  Container,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react';
import logger from '../utils/logger';
import {
  startSession,
  signUp,
  newUser,
  answerLocaleChallenge,
  getResponseUrl,
} from '../services/emailOTPAuth';
import { OTP_RETRY_COOKIE } from '../constants/cookies';
import usePageTitle from '../utils/usePageTitle';
import otpMessages from '../i18n/otp.messages';

import { getInitialLocaleSelection } from '../services/locale';
import TopHeader from '../components/TopHeader';
import ContainerLayout from '../components/ContainerLayout';
import Main from '../components/Main';
import Footer from '../components/Footer';
import { AppConfig } from '../types/app';
import styles from '../components/CardContainer.module.css';
import HelpLink from '../components/HelpLink';
import * as AuthService from '../services/emailOTPAuth';
import { AppURL } from '../constants/urls';
import { validateEmail } from '../utils/email';

function EmailOTPSignIn({ config }: { config: AppConfig }) {
  if (config.enableOtp === 'false' || !config.enableOtp) {
    throw new Error('Invalid path');
  }
  const { formatMessage } = useIntl();
  usePageTitle('One-time email passcode');
  const location = useLocation();
  const navigate = useNavigate();
  AuthService.init(config);

  // If OTP retry - Skip all email OTP UI, go directly to getResponseUrl with cached tokens
  const [isRetrying, setIsRetrying] = useState(false);
  useEffect(() => {
    const isOtpRetry = Cookies.get(OTP_RETRY_COOKIE) === 'true';
    if (isOtpRetry) {
      setIsRetrying(true);
      Cookies.remove(OTP_RETRY_COOKIE);

      const urlParams = new URLSearchParams(location.search);
      const redirectUriParam = urlParams.get('redirect_uri');
      const stateParam = urlParams.get('state');

      getResponseUrl(
        config,
        'invalidIdToken_useCached',
        'invalidAccessToken_useCached',
        redirectUriParam!,
        stateParam!
      )
        .then((responseUrl) => {
          window.location.assign(responseUrl);
        })
        .catch((err) => {
          setIsRetrying(false);
        });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hState = location.state;
  let headerText = formatMessage(otpMessages.header);
  let formText = formatMessage(otpMessages.description);
  let buttonLabel = formatMessage(otpMessages.buttonLabel);
  let emailError = '';
  let emailText = '';
  if (hState) {
    emailText = hState.userEmail;
    if (hState.sessionExpired) {
      headerText = formatMessage(otpMessages.headerSessionExpired);
      formText = formatMessage(otpMessages.descriptionSessionExpired);
      buttonLabel = formatMessage(otpMessages.buttonLabelSessionExpired);
    } else if (hState.otpError) {
      emailError = formatMessage(otpMessages.emailOTPError);
    }
  }
  const [emailValue, setEmailValue] = useState(emailText);
  const [emailEntered, setEmailEntered] = useState(emailText !== ''); // start true if redirected back with an email already entered
  const [emailFormatError, setEmailFormatError] = useState(emailError);
  const [loading, setLoading] = useState(false);

  const enableCookieComponent = Boolean(
    config?.enableCookieComponent === 'true'
  );

  const handleEmailChange = (e: any) => {
    if (validateEmail(e.detail.value)) {
      setEmailEntered(true);
      setEmailFormatError('');
    } else {
      setEmailEntered(false);
    }
    setEmailValue(e.detail.value);
  };

  const handleSendOTPClick = async (e?: CustomEvent) => {
    e?.preventDefault();
    if (!validateEmail(emailValue)) {
      setEmailFormatError(formatMessage(otpMessages.emailFormatError));
      return;
    }
    try {
      setLoading(true);
      const isNewUser = (await newUser(emailValue)) as boolean;
      if (isNewUser) {
        await signUp(emailValue);
      }
      const cognitoUser = await startSession(emailValue);
      await answerLocaleChallenge(getInitialLocaleSelection(), cognitoUser);
      setLoading(false);
      navigate(`${AppURL.OTPChallenge}${location.search}`, {
        state: {
          cognitoUserName: cognitoUser.getUsername(),
          cognitoSession: (cognitoUser as any).Session,
          userEmail: emailValue,
        },
      });
    } catch (err) {
      setEmailFormatError(formatMessage(otpMessages.emailOTPError));
      setLoading(false);
      logger.error(err);
    }
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    await handleSendOTPClick();
  };

  useEffect(() => {
    setEmailFormatError(emailError);
  }, [emailError]);

  if (isRetrying) {
    return (
      <>
        <TopHeader config={config} />
        <Main config={config}>
          <ContainerLayout enableResponsiveStyles>
            <ContentLayout>
              <Spinner size="large" data-testid="spinner" />
            </ContentLayout>
          </ContainerLayout>
        </Main>
        <Footer enableCookieComponent={enableCookieComponent} />
      </>
    );
  }

  return (
    <>
      <TopHeader config={config} />
      <Main config={config}>
        <ContainerLayout enableResponsiveStyles>
          <ContentLayout>
            <Container
              header={
                <Header
                  className={styles.header}
                  variant="h2"
                  headingTagOverride="h1"
                  description={formText}
                >
                  {headerText}
                </Header>
              }
            >
              <form onSubmit={handleSubmit}>
                <Form
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        data-testid="back-button"
                        onClick={() => navigate(-1)}
                        formAction="none"
                      >
                        {formatMessage(otpMessages.previousButtonLabel)}
                      </Button>
                      <Button
                        variant="primary"
                        data-testid="submit-button"
                        disabled={!emailEntered}
                        formAction="submit"
                        loading={loading}
                      >
                        {buttonLabel}
                      </Button>
                    </SpaceBetween>
                  }
                >
                  <SpaceBetween direction="vertical" size="s">
                    <FormField
                      errorText={emailFormatError}
                      stretch={true}
                      label={formatMessage(otpMessages.emailInputName)}
                    >
                      <Input
                        name="email"
                        type="email"
                        data-testid="text-input"
                        value={emailValue}
                        onChange={(event) => {
                          handleEmailChange(event);
                        }}
                        disabled={loading}
                        disableBrowserAutocorrect={true}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus={true} // OK to use since this is the only purpose of the page.
                        className={styles.mobileInput}
                        autoComplete="email"
                      />
                    </FormField>
                  </SpaceBetween>
                </Form>
              </form>
            </Container>
            <HelpLink />
          </ContentLayout>
        </ContainerLayout>
      </Main>
      <Footer enableCookieComponent={enableCookieComponent} />
    </>
  );
}

export default EmailOTPSignIn;
